import { useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry.tsx'
import { createSemaphore, callWithRetry } from '@/lib/semaphore'
import { normalizeImages, buildTemplateConfig } from '@/lib/imageUtils'
import { useDebounceWrite } from './useDebounceWrite'
import { usePipelinePlanner } from './usePipelinePlanner'
import { usePipelineGenerator } from './usePipelineGenerator'
import { useGenerationStore } from '@/stores/generationStore'
import type { Asset, Note, NoteImage, NoteTemplate, NoteImageSlot } from '@/types'
import type { UseImagePipelineOptions, ImagePlan, ImagePlanItem, BulkImageOptions } from '@/types/pipeline'

export function usePipelineCoordinator(options: UseImagePipelineOptions) {
  const {
    batchUpdateStatus,
    batchUpdateNotesWithImages,
    updateNoteStatus,
    updateNoteImages,
    markAssetsUsed,
    releaseAssetsFromNote,
    onProgressUpdate,
    onProgressClear,
    onStatusSync,
    onImagesSync,
    onNoteCompleted,
    onBeforeWrite,
    onAfterWrite,
  } = options

  const { planSingle, planBatch } = usePipelinePlanner()
  const { processImageItem, normalizeAndUploadReferenceImage } = usePipelineGenerator()

  // 本地图片缓存
  const smartImagesRef = useRef<Record<string, NoteImage[]>>({})

  // 防抖写入 Hook
  const { debouncedWrite, flush: flushDatabaseWrites } = useDebounceWrite<NoteImage[]>(
    updateNoteImages,
    {
      debounceDelay: 2000,
      retryDelay: 1500,
      onBeforeWrite,
      onAfterWrite,
    }
  )

  // 更新笔记图片（本地缓存 + 防抖写入）
  const updateImagesForNote = useCallback(
    async (noteId: string, updater: (prev: NoteImage[]) => NoteImage[]) => {
      const current = smartImagesRef.current[noteId] || []
      const next = normalizeImages(updater(current))

      smartImagesRef.current[noteId] = next
      onImagesSync?.(noteId, next)
      debouncedWrite(noteId, next)
    },
    [debouncedWrite, onImagesSync]
  )

  // 创建占位符
  const createPlaceholders = useCallback(
    async (noteId: string, planImages: ImagePlanItem[]): Promise<NoteImage[]> => {
      return Promise.all(
        planImages.map(async (item, idx) => {
          const productionMethod = item.production_method || 'collage'
          const normalizedReferenceImage = await normalizeAndUploadReferenceImage(
            item.reference_image,
            noteId,
            idx
          )
          return {
            index: item.index,
            type: item.type,
            source:
              productionMethod === 'asset'
                ? 'asset'
                : productionMethod.includes('ai')
                ? 'ai_generated'
                : 'collage',
            status: 'generating',
            asset_ids: item.asset_ids,
            generation_prompt: item.generation_prompt,
            image_text: item.image_text,
            reference_image_id: item.reference_image_id,
            reference_image: normalizedReferenceImage,
            production_method: productionMethod,
          } as NoteImage
        })
      )
    },
    [normalizeAndUploadReferenceImage]
  )

  // 单笔记智能配图
  const runSmartImages = useCallback(
    async (
      note: Note,
      templateId: string,
      templateById: Record<string, NoteTemplate>,
      slotMap: Record<string, NoteImageSlot[]>,
      analyzedAssets: Asset[],
      avoidDuplicates: boolean = false,
      customInstruction?: string
    ): Promise<void> => {
      const selectedTemplate = templateById[templateId]
      if (!selectedTemplate) throw new Error('选中的模版不存在')

      const selectedTemplateSlots = slotMap[templateId] || []
      const templateConfig = buildTemplateConfig(selectedTemplate, selectedTemplateSlots)

      const desiredCount = (() => {
        const min = Math.max(1, templateConfig.image_count_min || 1)
        const max = Math.max(min, templateConfig.image_count_max || min)
        const existed = note.images?.length && note.images.length > 0 ? note.images.length : max
        return Math.min(Math.max(existed, min), max)
      })()

      await releaseAssetsFromNote(note.id)

      const currentAvailableAssets = avoidDuplicates
        ? analyzedAssets.filter((a) => {
            const usedByOtherNotes = (a.usedInNoteIds || []).some((id) => id !== note.id)
            return !usedByOtherNotes
          })
        : analyzedAssets

      const plan = await planSingle(
        note,
        currentAvailableAssets,
        desiredCount,
        templateConfig,
        avoidDuplicates,
        customInstruction
      )

      if (plan.images) {
        const usedAssetIds = plan.images.flatMap((img) => img.asset_ids || []).filter(Boolean)
        if (usedAssetIds.length > 0) await markAssetsUsed(usedAssetIds, note.id)
      }

      const placeholders = await createPlaceholders(note.id, plan.images)
      smartImagesRef.current[note.id] = normalizeImages(placeholders)

      onBeforeWrite?.()
      await batchUpdateNotesWithImages([
        { noteId: note.id, status: 'generating', images: smartImagesRef.current[note.id] },
      ])
      onAfterWrite?.()

      onStatusSync?.(note.id, 'generating')
      onImagesSync?.(note.id, smartImagesRef.current[note.id])
      onProgressUpdate?.(note.id, 'generating', 0, plan.images.length)

      let hasFailure = false
      let completedCount = 0

      for (const item of plan.images) {
        // 【中止检查点】检查任务是否已被中止
        if (!useGenerationStore.getState().smartLoading[note.id]) {
          console.log(`[runSmartImages] 任务已中止: ${note.id}`)
          return
        }

        try {
          await processImageItem(note, item, analyzedAssets, updateImagesForNote)
          completedCount++
          onProgressUpdate?.(note.id, 'generating', completedCount, plan.images.length)
        } catch (err) {
          hasFailure = true
          captureError(err, { feature: 'image-pipeline', action: 'generate-single', noteId: note.id })
          await updateImagesForNote(note.id, (prev) =>
            prev.map((img) => (img.index === item.index ? { ...img, status: 'failed' } : img))
          )
          completedCount++
          onProgressUpdate?.(note.id, 'generating', completedCount, plan.images.length)
        }
      }

      await flushDatabaseWrites(note.id)

      if (hasFailure) {
        await updateNoteStatus(note.id, 'draft')
        onStatusSync?.(note.id, 'draft')
        toast.error('部分配图生成失败，请重试或调整素材')
      } else {
        await updateNoteStatus(note.id, 'completed')
        onStatusSync?.(note.id, 'completed')
        onNoteCompleted?.(note.id)
        toast.success('智能配图完成')
      }

      onProgressClear?.(note.id)
    },
    [
      planSingle,
      createPlaceholders,
      processImageItem,
      updateImagesForNote,
      flushDatabaseWrites,
      batchUpdateNotesWithImages,
      updateNoteStatus,
      markAssetsUsed,
      releaseAssetsFromNote,
      onProgressUpdate,
      onProgressClear,
      onStatusSync,
      onImagesSync,
      onNoteCompleted,
      onBeforeWrite,
      onAfterWrite,
    ]
  )

  // 批量配图
  const runBulkImages = useCallback(
    async (
      selectedNotes: Note[],
      templateId: string,
      templateById: Record<string, NoteTemplate>,
      slotMap: Record<string, NoteImageSlot[]>,
      analyzedAssets: Asset[],
      bulkOptions: BulkImageOptions = {}
    ): Promise<{ success: number; fail: number }> => {
      const { isResume = false, onWaitingIdsUpdate, avoidDuplicates = false, customInstruction } = bulkOptions

      const selectedTemplate = templateById[templateId]
      if (!selectedTemplate) throw new Error('选中的模版不存在')

      const selectedTemplateSlots = slotMap[templateId] || []
      const templateConfig = buildTemplateConfig(selectedTemplate, selectedTemplateSlots)

      onWaitingIdsUpdate?.(new Set(selectedNotes.map((n) => n.id)))

      // 如果不是恢复模式，释放素材占用
      if (!isResume) {
        await Promise.all(selectedNotes.map((note) => releaseAssetsFromNote(note.id)))
      }

      const PLAN_BATCH_SIZE = 4
      const IMAGE_CONCURRENCY = 4
      const ACQUIRE_TIMEOUT = 10 * 60 * 1000

      const desiredCount = Math.max(1, templateConfig.image_count_max || 1)
      const planBatches: (typeof selectedNotes)[] = []
      for (let i = 0; i < selectedNotes.length; i += PLAN_BATCH_SIZE) {
        planBatches.push(selectedNotes.slice(i, i + PLAN_BATCH_SIZE))
      }

      const semaphore = createSemaphore(IMAGE_CONCURRENCY)
      const allImagePromises: Promise<void>[] = []
      const targetNoteIds = new Set(selectedNotes.map((n) => n.id))
      const usedAssetIdsInCurrentRun = new Set<string>()

      const notePendingCount = new Map<string, number>()
      const noteFailureCount = new Map<string, number>()
      let totalSuccess = 0
      let totalFail = 0

      for (let batchIdx = 0; batchIdx < planBatches.length; batchIdx++) {
        const batch = planBatches[batchIdx]
        
        // 【中止检查点】过滤掉已经中止的任务
        const activeBatch = batch.filter(n => useGenerationStore.getState().smartLoading[n.id])
        if (activeBatch.length === 0 && batch.length > 0) {
          // 如果整批都中止了，直接跳过这一批
          onWaitingIdsUpdate?.((prev) => {
            const next = new Set(prev)
            batch.forEach((n) => next.delete(n.id))
            return next
          })
          continue
        }

        onWaitingIdsUpdate?.((prev) => {
          const next = new Set(prev)
          batch.forEach((n) => next.delete(n.id))
          return next
        })

        // 筛选出需要规划的笔记（非恢复模式，或者恢复模式但没有图片方案的）
        const notesToPlan = isResume 
          ? activeBatch.filter(n => !n.images || n.images.length === 0)
          : activeBatch

        if (notesToPlan.length > 0) {
          console.log(`[usePipelineCoordinator] 开始为批次规划配图, 笔记数量: ${notesToPlan.length}`);
          onBeforeWrite?.()
          await batchUpdateStatus(notesToPlan.map((n) => ({ noteId: n.id, status: 'planning' })))
          notesToPlan.forEach((n) => {
            onStatusSync?.(n.id, 'planning')
            onProgressUpdate?.(n.id, 'planning', 0, desiredCount)
          })
          onAfterWrite?.()

          let plans: ImagePlan[] = []
          try {
            const currentAvailableAssets = avoidDuplicates
              ? analyzedAssets.filter((a) => {
                  const usedByOtherNotes = (a.usedInNoteIds || []).some((id) => !targetNoteIds.has(id))
                  if (usedByOtherNotes) return false
                  if (usedAssetIdsInCurrentRun.has(a.id)) return false
                  return true
                })
              : analyzedAssets

            const planStartTime = Date.now();
            console.log(`[usePipelineCoordinator] 正在调用 planBatch...`);
            plans = await callWithRetry(
              () =>
                planBatch(
                  notesToPlan.map((n) => ({ note_id: n.id, title: n.title, content: n.content })),
                  currentAvailableAssets,
                  desiredCount,
                  templateConfig,
                  avoidDuplicates,
                  customInstruction
                ),
              {
                timeout: 180000,
                retries: 2,
                onRetry: (attempt, error) => {
                  console.warn(`[usePipelineCoordinator] planBatch 重试第 ${attempt} 次, 错误: ${error.message}`);
                },
              }
            )
            console.log(`[usePipelineCoordinator] 批次规划完成, 耗时: ${Date.now() - planStartTime}ms, 返回 ${plans?.length || 0} 个规划`);
          } catch (err) {
            console.error('[usePipelineCoordinator] 批量规划失败, 尝试逐篇规划:', err)
            plans = []
            // ... (逐篇规划逻辑保持不变，但增加日志)
            for (const note of notesToPlan) {
              // 【中止检查点】
              if (!useGenerationStore.getState().smartLoading[note.id]) {
                plans.push(null as unknown as ImagePlan)
                continue
              }
              try {
                const currentAvailableAssets = avoidDuplicates
                  ? analyzedAssets.filter((a) => {
                      const usedByOtherNotes = (a.usedInNoteIds || []).some((id) => !targetNoteIds.has(id))
                      if (usedByOtherNotes) return false
                      if (usedAssetIdsInCurrentRun.has(a.id)) return false
                      return true
                    })
                  : analyzedAssets
                const p = await callWithRetry(
                    () =>
                      planSingle(note, currentAvailableAssets, desiredCount, templateConfig, avoidDuplicates, customInstruction),
                    { timeout: 180000, retries: 2 }
                  )
                plans.push(p)
              } catch (e) {
                console.error(`[usePipelineCoordinator] 笔记 ${note.id} 逐篇规划也失败:`, e);
                plans.push(null as unknown as ImagePlan)
                onStatusSync?.(note.id, 'draft')
                onProgressClear?.(note.id)
                // 也要同步到数据库，否则状态会卡在 planning
                await updateNoteStatus(note.id, 'draft').catch(console.error);
              }
            }
          }

          const batchUpdates: Array<{ noteId: string; status: Note['status']; images: NoteImage[] }> = []
          for (let i = 0; i < notesToPlan.length; i++) {
            const note = notesToPlan[i]
            // 【中止检查点】
            if (!useGenerationStore.getState().smartLoading[note.id]) continue;

            const plan = plans[i]
            if (plan?.images) {
              console.log(`[usePipelineCoordinator] 正在为笔记 ${note.id} 创建占位符...`);
              const usedAssetIds = plan.images.flatMap((img) => img.asset_ids || []).filter(Boolean)
              if (usedAssetIds.length > 0) {
                await markAssetsUsed(usedAssetIds, note.id)
                if (avoidDuplicates) usedAssetIds.forEach((id) => usedAssetIdsInCurrentRun.add(id))
              }
              
              const placeholders = await createPlaceholders(note.id, plan.images)
              smartImagesRef.current[note.id] = normalizeImages(placeholders)
              batchUpdates.push({
                noteId: note.id,
                status: 'generating',
                images: smartImagesRef.current[note.id],
              })
            } else {
              console.warn(`[usePipelineCoordinator] 笔记 ${note.id} 没有生成有效的配图方案`);
              totalFail++
            }
          }

          if (batchUpdates.length > 0) {
            console.log(`[usePipelineCoordinator] 正在批量更新笔记状态为 generating, 数量: ${batchUpdates.length}`);
            onBeforeWrite?.()
            await batchUpdateNotesWithImages(batchUpdates)
            onAfterWrite?.()
            
            // 🔥 重要：状态更新后，立即刷新各笔记的进度，重置 UI 超时计时器
            batchUpdates.forEach(update => {
              // 此时已经从 planning 变为 generating (或准备进入)
              // 我们先同步 UI 状态
              onStatusSync?.(update.noteId, 'generating')
              // 更新进度对象，特别是更新 lastUpdateTime
              onProgressUpdate?.(update.noteId, 'generating', 0, update.images?.length || desiredCount)
            })
          }
        }

        // 处理所有笔记的生成阶段（包括恢复模式下已有方案的）
        for (const note of batch) {
          // 初始化本地缓存
          if (!smartImagesRef.current[note.id]) {
            smartImagesRef.current[note.id] = normalizeImages(note.images || [])
          }
          
          const images = smartImagesRef.current[note.id]
          if (!images || images.length === 0) continue

          const pendingImages = images.filter(img => img.status !== 'completed')
          const totalImages = images.length
          const initialCompleted = images.length - pendingImages.length

          notePendingCount.set(note.id, pendingImages.length)
          noteFailureCount.set(note.id, 0)
          
          if (note.status !== 'generating') {
            onStatusSync?.(note.id, 'generating')
          }
          onImagesSync?.(note.id, images)
          onProgressUpdate?.(note.id, 'generating', initialCompleted, totalImages)

          if (pendingImages.length === 0) {
            // 如果全部已完成，直接标记成功
            if (note.status !== 'completed') {
              await updateNoteStatus(note.id, 'completed')
              onStatusSync?.(note.id, 'completed')
              onNoteCompleted?.(note.id)
            }
            totalSuccess++
            onProgressClear?.(note.id)
            continue
          }

          pendingImages.forEach((item) => {
            const task = (async () => {
              // 【中止检查点】在获取信号量前检查
              if (!useGenerationStore.getState().smartLoading[note.id]) {
                return
              }

              await semaphore.acquire(ACQUIRE_TIMEOUT)
              try {
                // 【中止检查点】在开始处理前检查
                if (!useGenerationStore.getState().smartLoading[note.id]) {
                  return
                }

                await processImageItem(note, item, analyzedAssets, updateImagesForNote)
              } catch (err) {
                console.error('[配图生成失败]', note.id, item.index, err)
                await updateImagesForNote(note.id, (prev) =>
                  prev.map((img) => (img.index === item.index ? { ...img, status: 'failed' } : img))
                )
                noteFailureCount.set(note.id, (noteFailureCount.get(note.id) || 0) + 1)
              } finally {
                semaphore.release()
                const rem = (notePendingCount.get(note.id) || 1) - 1
                notePendingCount.set(note.id, rem)
                onProgressUpdate?.(note.id, 'generating', totalImages - rem, totalImages)
                
                if (rem === 0) {
                  await flushDatabaseWrites(note.id)
                  const fc = noteFailureCount.get(note.id) || 0
                  const status = fc > 0 ? 'draft' : 'completed'
                  await updateNoteStatus(note.id, status)
                  onStatusSync?.(note.id, status)
                  if (status === 'completed') {
                    onNoteCompleted?.(note.id)
                    totalSuccess++
                  } else {
                    totalFail++
                  }
                  onProgressClear?.(note.id)
                }
              }
            })()
            allImagePromises.push(task)
          })
        }
      }

      await Promise.all(allImagePromises)
      onWaitingIdsUpdate?.(new Set())
      return { success: totalSuccess, fail: totalFail }
    },
    [
      planSingle,
      planBatch,
      createPlaceholders,
      processImageItem,
      updateImagesForNote,
      flushDatabaseWrites,
      batchUpdateStatus,
      batchUpdateNotesWithImages,
      updateNoteStatus,
      markAssetsUsed,
      releaseAssetsFromNote,
      onProgressUpdate,
      onProgressClear,
      onStatusSync,
      onImagesSync,
      onNoteCompleted,
      onBeforeWrite,
      onAfterWrite,
    ]
  )

  return { runSmartImages, runBulkImages, flushWrites: flushDatabaseWrites }
}
